import React, { createContext, useContext, useEffect, useState } from 'react';

import snapshot from '../content/snapshot.json';
import { CONTENT_QUERY, isUsableContent } from '../lib/queries';
import { isSanityConfigured, sanityClient } from '../lib/sanity';

/**
 * Content for every card.
 *
 * Starts from the build-time snapshot, which is bundled, so the first render
 * already has everything and no card needs a loading state. The live query then
 * runs on mount and replaces it, which is what makes a Studio edit appear
 * without a deploy. If Sanity is unreachable the snapshot simply stays.
 *
 * src/content/snapshot.json is generated by scripts/fetch-content-snapshot.js
 * on prebuild and prestart. It is gitignored.
 */
export const ContentContext = createContext(snapshot);

export function ContentProvider({ children, initialContent = snapshot }) {
  const [content, setContent] = useState(initialContent);

  useEffect(() => {
    if (!isSanityConfigured || !sanityClient) return undefined;

    let cancelled = false;

    sanityClient
      .fetch(CONTENT_QUERY)
      .then((live) => {
        if (cancelled) return;
        if (isUsableContent(live)) {
          setContent(live);
        } else {
          console.warn('[content] Live query returned incomplete content; keeping the snapshot.');
        }
      })
      .catch((error) => {
        console.warn(`[content] Live query failed (${error.message}); keeping the snapshot.`);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return <ContentContext.Provider value={content}>{children}</ContentContext.Provider>;
}

export function useContent() {
  return useContext(ContentContext);
}

export function useSettings() {
  return useContent().settings;
}

/** Interface copy: button labels, hints and messages. */
export function useUiText() {
  return useContent().ui;
}

/** Card heading for a layout card type, falling back to the key itself. */
export function useCardTitle(cardType) {
  const ui = useUiText();
  const match = ui?.cardTitles?.find((entry) => entry.key === cardType);
  return match?.label ?? cardType;
}
